import { parseISO } from 'date-fns';
import invariant from 'tiny-invariant';

/**
 * Convert a date-like value coming from Bryntum, API responses, or
 * intermediate state into a concrete `Date`.
 *
 * Fails loudly (tiny-invariant) when the input is `null`/`undefined`/invalid
 * rather than silently producing `Invalid Date`, which is what V1 did via
 * `helpers/DateMapper.ts`.
 */
export function mapDate(value: unknown): Date {
  invariant(
    value !== null && value !== undefined && value !== '',
    'Cannot map date: value is empty'
  );

  if (value instanceof Date) {
    invariant(
      !Number.isNaN(value.getTime()),
      'Cannot map date: received Invalid Date'
    );
    return value;
  }

  if (typeof value === 'string') {
    const parsed = parseISO(value);
    invariant(
      !Number.isNaN(parsed.getTime()),
      `Cannot map date: failed to parse "${value}"`
    );
    return parsed;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    invariant(
      !Number.isNaN(date.getTime()),
      `Cannot map date: invalid numeric timestamp ${value}`
    );
    return date;
  }

  throw new Error(`Cannot map date: unsupported type ${typeof value}`);
}
